
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <Card>
          <CardContent className="p-8 prose prose-gray dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Introduction</h2>
              <p>
                GoDyspo ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our real estate investment platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Information We Collect</h2>
              <h3 className="text-lg font-medium mb-2">Personal Information</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Name, email address, and phone number</li>
                <li>Business information and company details</li>
                <li>Investment preferences and property criteria</li>
                <li>Financial information related to property transactions</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2">Usage Information</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Property searches and viewing history</li>
                <li>Messages and communications on our platform</li>
                <li>Device information and IP addresses</li>
                <li>Browser type and operating system</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6">
                <li>To provide and maintain our services</li>
                <li>To match you with relevant investment opportunities</li>
                <li>To facilitate communication between buyers and sellers</li>
                <li>To send notifications about new properties and updates</li>
                <li>To improve our platform and user experience</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Information Sharing</h2>
              <p className="mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6">
                <li>Other users as necessary for property transactions</li>
                <li>Service providers who assist us in operating our platform</li>
                <li>Legal authorities when required by law</li>
                <li>Business partners for legitimate business purposes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Third-Party Services and Integrations</h2>
              <p className="mb-4">We use the following third-party services to operate our platform:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Stripe:</strong> Payment processing for subscriptions and transactions. View their <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a></li>
                <li><strong>Google Maps:</strong> Location services and property mapping. View their <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a></li>
                <li><strong>Supabase:</strong> Database and infrastructure services. View their <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a></li>
                <li><strong>Twilio:</strong> SMS and text messaging services. View their <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a></li>
              </ul>
              <p>
                These service providers have access only to the information necessary to perform their functions and are obligated to maintain the confidentiality and security of your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Data Retention</h2>
              <p className="mb-4">We retain your personal information for as long as necessary to provide our services and comply with legal obligations:</p>
              <ul className="list-disc pl-6">
                <li><strong>Active Accounts:</strong> Your data is retained while your account remains active</li>
                <li><strong>Closed Accounts:</strong> Data is retained for 90 days after account closure for backup and legal purposes, then permanently deleted</li>
                <li><strong>Transaction Records:</strong> Financial and transaction records are retained for 7 years to comply with legal and tax requirements</li>
                <li><strong>Marketing Preferences:</strong> Communication preferences are retained until you opt out or close your account</li>
                <li><strong>Legal Holds:</strong> Data may be retained longer if required by law, regulation, or legal process</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Data Breach Notification</h2>
              <p className="mb-4">
                In the event of a data breach that compromises your personal information, we will:
              </p>
              <ul className="list-disc pl-6">
                <li>Notify affected users within 72 hours of discovering the breach</li>
                <li>Provide clear information about what data was affected</li>
                <li>Explain the steps we're taking to address the breach</li>
                <li>Recommend actions you should take to protect yourself</li>
                <li>Notify relevant regulatory authorities as required by law</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Your Rights</h2>
              <p className="mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Update or correct inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
                <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Objection:</strong> Object to processing of your data for certain purposes</li>
                <li><strong>Restriction:</strong> Request limitation of how we process your data</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at support@godyspo.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">California Privacy Rights (CCPA/CPRA)</h2>
              <p className="mb-4">
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Right to Know:</strong> Request details about the personal information we collect, use, and share</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of the sale or sharing of your personal information</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Right to Limit:</strong> Limit the use of sensitive personal information</li>
                <li><strong>Right to Non-Discrimination:</strong> Receive equal service and pricing regardless of exercising your privacy rights</li>
              </ul>
              <p className="mb-4">
                <strong>Important:</strong> We do not sell your personal information to third parties. We do not share personal information for cross-context behavioral advertising.
              </p>
              <p>
                California residents can exercise these rights by contacting us at support@godyspo.com or through your account settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">International Data Transfers</h2>
              <p className="mb-4">
                GoDyspo operates primarily in the United States, and your information is stored on servers located in the United States. If you access our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States.
              </p>
              <p>
                We ensure appropriate safeguards are in place to protect your personal information in accordance with this Privacy Policy and applicable data protection laws, including the use of standard contractual clauses approved by regulatory authorities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Children's Privacy</h2>
              <p className="mb-4">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. Our Terms and Conditions require all users to be at least 18 years of age.
              </p>
              <p>
                If we become aware that we have collected personal information from a child under 18, we will take immediate steps to delete that information. If you believe we have collected information from a child, please contact us immediately at support@godyspo.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Cookies and Tracking Technologies</h2>
              <p className="mb-4">
                We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and provide personalized content. Cookies are small text files stored on your device that help us remember your preferences and understand how you use our platform.
              </p>
              <h3 className="text-lg font-medium mb-2">Types of Cookies We Use</h3>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Essential Cookies:</strong> Necessary for the platform to function properly (authentication, security)</li>
                <li><strong>Performance Cookies:</strong> Help us understand how visitors interact with our platform</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Collect information about platform usage and performance</li>
              </ul>
              <p>
                You can control cookie preferences through your browser settings. Note that disabling certain cookies may limit platform functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">SMS and Text Messaging</h2>
              <p className="mb-4">
                If you provide your mobile phone number and consent to receive text messages, we may send you SMS notifications related to your account, property alerts, and platform communications. For detailed information about our text messaging practices, please see the "SMS and Messaging Communications Consent" section in our <a href="/terms-and-conditions" className="text-primary hover:underline">Terms and Conditions</a>.
              </p>
              <p className="mb-4">
                <strong>How We Use Your Phone Number:</strong>
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Account verification and two-factor authentication</li>
                <li>Property match alerts and notifications</li>
                <li>Platform-to-user communications</li>
                <li>User-to-user messaging facilitation</li>
                <li>Important account and security updates</li>
              </ul>
              <p className="mb-4">
                We use Twilio as our SMS service provider. Your phone number is shared with Twilio solely for message delivery purposes. Twilio's privacy policy is available at <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.twilio.com/legal/privacy</a>.
              </p>
              <p>
                You can opt out of non-essential text messages at any time by replying STOP to any message or adjusting your notification preferences in your account settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Automated Decision Making</h2>
              <p className="mb-4">
                We use automated algorithms and systems to enhance your experience on our platform, including:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Property Matching:</strong> Algorithms match properties to your stated investment preferences and search criteria</li>
                <li><strong>Recommendations:</strong> Suggest properties and sellers based on your browsing history and saved items</li>
                <li><strong>Search Results:</strong> Prioritize and rank property listings based on relevance to your profile</li>
              </ul>
              <p className="mb-4">
                <strong>Your Rights:</strong> You have the right to understand how these automated systems work and how they affect the content you see. We do not use automated decision-making for decisions that produce legal effects or similarly significantly affect you without human oversight.
              </p>
              <p>
                You can always bypass automated recommendations by using manual search and filtering options. Contact support@godyspo.com if you have questions about our matching algorithms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes to this policy, we will:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Update the "Last Updated" date at the top of this page</li>
                <li>Notify you via email at the address associated with your account</li>
                <li>Display a prominent notice on our platform</li>
                <li>Request your consent if required by applicable law</li>
              </ul>
              <p>
                We encourage you to review this Privacy Policy periodically. Your continued use of our services after changes are posted constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Legal Basis for Processing (GDPR)</h2>
              <p className="mb-4">
                If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, we process your personal information based on the following legal grounds:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Contract Performance:</strong> Processing necessary to provide our services and fulfill our contractual obligations to you</li>
                <li><strong>Consent:</strong> You have given explicit consent for specific processing activities (e.g., marketing communications, optional features)</li>
                <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests, such as:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Fraud prevention and security</li>
                    <li>Platform improvement and analytics</li>
                    <li>Customer support and communication</li>
                    <li>Network and information security</li>
                  </ul>
                </li>
                <li><strong>Legal Obligations:</strong> Processing required to comply with applicable laws, regulations, and legal processes</li>
              </ul>
              <p>
                You have the right to object to processing based on legitimate interests. Contact us at support@godyspo.com to exercise this right.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
              <p className="mb-4">
                If you have questions about this Privacy Policy, wish to exercise your privacy rights, or need assistance with your personal information, please contact us at:
              </p>
              <p>
                Email: <a href="mailto:support@godyspo.com" className="text-primary hover:underline">support@godyspo.com</a>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
